import React, { useEffect, useState } from "react";
import EditProfileAdmin from "./EditProfileAdmin";
import EditProfileNgo from "./EditProfileNgo";
import EditProfileNgoMember from "./EditProfileNgoMember";
import EditProfileDonor from "./EditProfileDonor";
import EditProfileHumanitarian from "./EditProfileHumanitarian";
import AuthService from "../../base/services/authentication.service";
import { UserRolesEnum } from "../auth/userRoles.enum";
import { httpService } from "../../base/services/httpService.service";
import { Form, Formik } from "formik";
import { useNotification } from "../../base/hooks/useNotification";
import { Button } from "antd";
import dayjs from "dayjs";
import "./style.scss";
import { useResultStore } from "../../../_metronic/layout/components/useResultStore";
import { useTranslation } from "../../i18n/LanguageContext";

import {
   getAdminValidationSchema,
   getNgoValidationSchema,
   getNgoMemberValidationSchema,
   getDonorValidationSchema,
   getHumanitarianValidationSchema
} from "./validation/validationSchema";
import language from "react-syntax-highlighter/dist/esm/languages/hljs/1c";

const EditProfileComponents = ({ isEditMode, handleCloseEditForm, handleSaveClick }) => {
   const { t } = useTranslation();
   const { api: notificationApi, notificationContextHolder } = useNotification();
   const [loading, setLoading] = useState(false);
   const { setFullNameChanged, fullNameChanged } = useResultStore();
   const isDonor = AuthService.isAuthorized([UserRolesEnum.Donor]);
   const isSuperAdmin = AuthService.isAuthorized([UserRolesEnum.SuperAdmin]);
   const isNgo = AuthService.isAuthorized([UserRolesEnum.NationalOrganization]);
   const isNgoMember = AuthService.isAuthorized([UserRolesEnum.Staff]);
   const isHumanitarian = AuthService.isAuthorized([UserRolesEnum.Consultant]);
   const isManager = AuthService.isAuthorized([UserRolesEnum.Manager]);

   const [countryList, setCountryList] = useState([]);
   const [cityList, setCityList] = useState([]);
   const [genders, setGenders] = useState([]);

   const [selectedCountry, setSelectedCountry] = useState(null);
   const [selectedCity, setSelectedCity] = useState(null);

   const [languageOptions, setLanguageOptions] = useState([]);
   const getValidationSchema = () => {
      if (isSuperAdmin) {
         return getAdminValidationSchema(t);
      } else if (isNgo) {
         return getNgoValidationSchema(t);
      } else if (isNgoMember || isManager) {
         return getNgoMemberValidationSchema(t);
      } else if (isDonor) {
         return getDonorValidationSchema(t);
      } else if (isHumanitarian) {
         return getHumanitarianValidationSchema(t);
      }
   };

   const validationSchema = getValidationSchema();

   const [formInitialValues, setFormInitialValues] = useState({
      name: "",
      country: "",
      address: "",
      city: "",
      distric: "",
      locationType: "",
      phoneNumber: "",
      provice: "",
      subdistric: "",
      village: "",
      zipCode: "",
      birthday: null,
      genderId: "",
      gender: "",
      organizationName: "",
      organizationEmail: "",
      linkedIn: "",
      likedInProfile: "",
      nationalityId: "",
      nationality: "",
      operatingCountriesIds: [],
      operatingCountries: "",
      personalStatement: "",
      residenceCountryId: "",
      residenceCountry: "",
      baseCountryId: null,
      cityId: null,
      certificate: "",
      cv: null,
      educationalInstitution: "",
      higestDegree: null,
      expertises: [],
      languages: [],
      studyFields: ""
   });

   const changeNameLocal = (name) => {
      if (name) {
         AuthService.setFullName(name);
         setFullNameChanged(!fullNameChanged);
      }
   };

   const getProfileAdmin = () => {
      httpService.get(
         `Accounts/admin-profile`,
         (res) => {
            const profileInfo = res?.data;

            setFormInitialValues({
               name: profileInfo?.name,
               phoneNumber: profileInfo?.phoneNumber
            });
            setLoading(false);
         },
         () => {
            setLoading(false);
         }
      );
   };

   const editProfileAdmin = async (values) => {
      try {
         const updatedValues = {
            name: values?.name,
            phoneNumber: values?.phoneNumber
         };
         setLoading(true);
         await httpService.put(
            `Accounts/admin-profile`,
            updatedValues,
            (res) => {
               setFormInitialValues(res?.data);
               changeNameLocal(values?.name);
               handleSaveClick();
               notificationApi.success("user", {
                  message: t("profile-updated-success-title"),
                  description: t("profile-updated-success-description")
               });
            },
            () => {
               setLoading(false);
            }
         );
         getProfileAdmin();
      } catch (error) {
         notificationApi.error("user", {
            message: t("generic-problem-occurred-title"),
            description: t("profile-update-error-description")
         });
      } finally {
         setLoading(false);
      }
   };

   const getProfileDonor = () => {
      httpService.get(
         `Accounts/donor-profile`,
         (res) => {
            const profileInfo = res?.data;

            setFormInitialValues({
               ...profileInfo,
               baseCountryId: profileInfo?.country?.id
            });
            setLoading(false);
         },
         () => {
            setLoading(false);
         }
      );
   };

   // edit profile for donor
   const editProfileDonor = async (values) => {
      try {
         const baseCountryIdValue = values.baseCountryId;
         const updatedValues = {
            baseCountryId: baseCountryIdValue,
            name: values?.name
         };

         setLoading(true);
         await httpService.put(
            `Accounts/donor-profile`,
            updatedValues,
            (res) => {
               setFormInitialValues(res?.data);
               changeNameLocal(values?.name);
               handleSaveClick();
               notificationApi.success("user", {
                  message: t("profile-updated-success-title"),
                  description: t("profile-updated-success-description")
               });
            },
            () => {
               setLoading(false);
            }
         );
         getProfileDonor();
      } catch (error) {
         notificationApi.error("user", {
            message: t("generic-problem-occurred-title"),
            description: t("profile-update-error-description")
         });
      } finally {
         setLoading(false);
      }
   };

   const getProfileConsultant = () => {
      httpService.get(
         `Accounts/consultant-profile`,
         (res) => {
            const profileInfo = res?.data;
            setFormInitialValues({
               name: profileInfo?.name || "",
               personalStatement: profileInfo?.personalStatement || "",
               nationality: profileInfo?.nationality?.name || "",
               nationalityId: profileInfo?.nationality?.id || "",
               residenceCountry: profileInfo?.residenceCountry?.name || "",
               residenceCountryId: profileInfo?.residenceCountry?.id || "",

               operatingCountriesIds: profileInfo?.operatingCountries?.map((country) => ({
                  value: country?.id,
                  label: country?.name
               })),
               operatingCountries: profileInfo?.operatingCountries?.map((country) => ({
                  value: country?.id,
                  label: country?.name
               })),
               linkedIn: profileInfo?.linkedIn || "",
               phoneNumber: profileInfo?.phoneNumber || "",
               higestDegree: profileInfo?.higestDegree
                  ? {
                       value: profileInfo.higestDegree.id,
                       label: profileInfo.higestDegree.name
                    }
                  : null,
               studyFields: profileInfo.studyFields,
               educationalInstitution: profileInfo.educationalInstitution,
               expertises: profileInfo.expertises.map((expertise) => ({
                  areaName: expertise.areaName?.trim(),
                  areaExperienceYears: parseInt(expertise.areaExperienceYears, 10)
               })),
               languages: profileInfo.languages.map((lang) => ({
                  languageId: lang.languageId?.trim(),
                  level: parseInt(lang.level, 10)
               })),
               cv: profileInfo.cv || "",
               certificate: profileInfo.certificate || ""
            });

            setLoading(false);
         },
         () => {
            setLoading(false);
         }
      );
   };

   const editProfileConsultant = async (values) => {
      try {
         const operatingCountriesIdsValue =
            values?.operatingCountriesIds?.map((country) =>
               typeof country === "object" ? country?.value : country
            ) || [];

         const languagesPayload = values?.languages
            ?.filter((language) => language?.languageId?.trim() && language?.level)
            .map((language) => ({
               languageId: language?.languageId?.trim(),
               level: parseInt(language?.level, 10) || null
            }));

         const expertisesPayload = values?.expertises
            ?.filter(
               (expertise) =>
                  expertise?.areaName?.trim() &&
                  expertise?.areaExperienceYears !== "" &&
                  expertise?.areaExperienceYears !== null
            )
            .map((expertise) => ({
               areaName: expertise?.areaName?.trim(),
               areaExperienceYears: expertise?.areaExperienceYears
            }));

         const formData = new FormData();
         formData.append("Name", values?.name);
         formData.append("NationalityId", values?.nationalityId);
         formData.append("ResidenceCountryId", values?.residenceCountryId);

         formData.append("LinkedIn", values?.linkedIn);
         formData.append("PhoneNumber", values?.phoneNumber);
         formData.append("PersonalStatement", values?.personalStatement);

         formData.append(
            "HigestDegree",
            typeof values.higestDegree === "object" && values.higestDegree !== null
               ? values.higestDegree.value
               : values.higestDegree
         );

         formData.append("StudyFields", values?.studyFields);
         formData.append("EducationalInstitution", values?.educationalInstitution);

         operatingCountriesIdsValue.forEach((countryId, index) => {
            formData.append(`OperatingCountriesIds[${index}]`, countryId);
         });

         if (languagesPayload) {
            languagesPayload.forEach((language, index) => {
               formData.append(`Languages[${index}][languageId]`, language.languageId);
               formData.append(`Languages[${index}][level]`, language.level);
            });
         }

         // Append filtered and valid expertises
         if (expertisesPayload) {
            expertisesPayload.forEach((expertise, index) => {
               formData.append(`Expertises[${index}][areaName]`, expertise.areaName);
               formData.append(
                  `Expertises[${index}][areaExperienceYears]`,
                  expertise.areaExperienceYears
               );
            });
         }
         formData.append("CV", values?.cv);
         formData.append("Certificate", values?.certificate);

         setLoading(true);
         await httpService.put(
            `Accounts/consultant-profile`,
            formData,
            (res) => {
               setFormInitialValues(res?.data);
               changeNameLocal(formData.get("Name"));
               handleSaveClick();
               notificationApi.success("user", {
                  message: t("profile-updated-success-title"),
                  description: t("profile-updated-success-description")
               });
            },
            () => {
               setLoading(false);
            }
         );
         getProfileConsultant();
      } catch (error) {
         notificationApi.error("user", {
            message: t("generic-problem-occurred-title"),
            description: t("profile-update-error-description")
         });
      } finally {
         setLoading(false);
      }
   };

   const getProfileStaffMember = () => {
      httpService.get(
         `Accounts/ngo-member-profile`,
         (res) => {
            const profileInfo = res?.data;

            setFormInitialValues({
               name: profileInfo?.name || "",
               birthday: profileInfo?.birthday
                  ? dayjs(profileInfo.birthday).format("YYYY-MM-DD")
                  : "",
               gender: profileInfo?.gender?.name || "",
               genderId: profileInfo?.gender?.id || ""
            });

            setLoading(false);
         },
         () => {
            setLoading(false);
         }
      );
   };

   const editProfileStaffMember = async (values) => {
      try {
         const updatedValues = {
            name: values?.name,
            genderId: values?.genderId,
            birthday:
               values?.birthday && dayjs(values.birthday).isValid()
                  ? dayjs(values.birthday).format("YYYY-MM-DD")
                  : values.birthday
         };
         setLoading(true);
         await httpService.put(
            `Accounts/ngo-member-profile`,
            updatedValues,
            (res) => {
               setFormInitialValues(res?.data);
               changeNameLocal(values?.name);
               handleSaveClick();
               notificationApi.success("user", {
                  message: t("profile-updated-success-title"),
                  description: t("profile-updated-success-description")
               });
            },
            (error) => {
               setLoading(false);
            }
         );
         getProfileStaffMember();
      } catch (error) {
         notificationApi.error("user", {
            message: t("generic-problem-occurred-title"),
            description: t("profile-update-error-description")
         });
      } finally {
         setLoading(false);
      }
   };

   const getProfileNgo = () => {
      httpService.get(
         `Accounts/ngo-profile`,
         (res) => {
            const profileInfo = res?.data;
            setFormInitialValues({
               organizationName: profileInfo?.organizationName || "",
               country: profileInfo?.country?.name || "",
               baseCountryId: profileInfo?.country?.id,
               city: profileInfo?.city || "",
               cityId: profileInfo?.city?.id,
               organizationEmail: profileInfo?.organizationEmail,
               address: profileInfo?.address,
               zipCode: profileInfo?.zipCode
            });
            setSelectedCountry(profileInfo?.country?.id);
            setSelectedCity(profileInfo?.city?.id);

            setLoading(false);
         },
         () => {
            setLoading(false);
         }
      );
   };

   const editProfileNgo = async (values) => {
      try {
         const updatedValues = {
            organizationName: values?.organizationName,
            organizationEmail: values?.organizationEmail,
            baseCountryId: values?.baseCountryId,
            cityId: values?.cityId,
            address: values?.address,
            zipCode: values?.zipCode
         };
         setLoading(true);
         await httpService.put(
            `Accounts/ngo-profile`,
            updatedValues,
            (res) => {
               setFormInitialValues(res?.data);
               changeNameLocal(values?.organizationName);
               handleSaveClick();
               notificationApi.success("user", {
                  message: t("profile-updated-success-title"),
                  description: t("profile-updated-success-description")
               });
            },
            () => {
               setLoading(false);
            }
         );
         getProfileNgo();
      } catch (error) {
         notificationApi.error("user", {
            message: t("generic-problem-occurred-title"),
            description: t("profile-update-error-description")
         });
      } finally {
         setLoading(false);
      }
   };

   const handleOperatingCountries = (selectedCountries, values) => {
      const countryIds = selectedCountries?.map((country) => country?.value);
      values.operatingCountries = countryIds;
   };

   useEffect(() => {
      if (isSuperAdmin) {
         getProfileAdmin();
      } else if (isNgo) {
         getProfileNgo();
         getCountries();
      } else if (isHumanitarian) {
         getProfileConsultant();
         getCountries();
      } else if (isDonor) {
         getProfileDonor();
         getCountries();
      } else if (isNgoMember || isManager) {
         getProfileStaffMember();
         getGenders();
      }
   }, []);

   useEffect(() => {
      if (selectedCountry) {
         httpService.get(`/countries/${selectedCountry}/cities`, (res) => {
            const cityData = res.data.map((city) => ({
               value: city.id,
               label: city.name
            }));
            setCityList(cityData);
         });
      }
   }, [selectedCountry]);

   const [operatingCountriesList, setOperatingCountriesList] = useState([]);
   const [countriesOfResidence, setCountriesOfResidence] = useState([]);

   const getCountries = async () => {
      httpService.get(`/countries`, (data) => {
         const countryData = data.data.map((country) => {
            return {
               value: country.id,
               label: country.name
            };
         });
         setCountryList(countryData);
         setOperatingCountriesList(countryData);
         setCountriesOfResidence(countryData);
      });
   };

   const getGenders = async () => {
      // Kept as raw {value, name} pairs (not pre-translated into `label`) so
      // the consuming dropdown (EditProfileNgoMember.js) re-translates on a
      // language switch without a refetch.
      httpService.get("/StaffManagement/genders", (res) => {
         const data = res.data.map((gender) => {
            return {
               value: gender.id,
               name: gender.name
            };
         });
         setGenders(data);
      });
   };

   const [nations, setNations] = useState();
   useEffect(() => {
      httpService.get("/Countries/nationalities", (res) => {
         const data = res.data.map((nation) => {
            return {
               value: nation.id,
               label: nation.name
            };
         });
         setNations(data);
      });
   }, []);

   const [languageLevelOptions, setLanguageLevelOptions] = useState([]);

   useEffect(() => {
      httpService.get(`/Countries/languages`, (data) => {
         const languageData = data.data.map((language) => ({
            value: language.id,
            label: language.name
         }));
         setLanguageOptions(languageData);
      });

      httpService.get(`/Accounts/language-level`, (data) => {
         const languageLevelData = Object.entries(data.data).map(([value, label]) => ({
            value,
            label
         }));
         setLanguageLevelOptions(languageLevelData);
      });
   }, []);

   return (
      <>
         {notificationContextHolder}
         <Formik
            initialValues={formInitialValues}
            validationSchema={isEditMode && validationSchema}
            onSubmit={(values) => {
               if (isSuperAdmin) {
                  editProfileAdmin(values);
               } else if (isNgo) {
                  editProfileNgo(values);
               } else if (isNgoMember || isManager) {
                  editProfileStaffMember(values);
               } else if (isDonor) {
                  editProfileDonor(values);
               } else if (isHumanitarian) {
                  editProfileConsultant(values);
               }
            }}
            enableReinitialize
         >
            {({
               handleSubmit,
               handleChange,
               touched,
               values,
               errors,
               dirty,
               setFieldValue,
               isSubmitting,
               resetForm
            }) => (
               <Form id="editProfileForm" className="details-form">
                  <div className="profile-grid">
                     {isSuperAdmin && (
                        <EditProfileAdmin
                           {...{ isEditMode, handleChange, values, setFieldValue, errors }}
                        />
                     )}
                     {isNgo && (
                        <EditProfileNgo
                           {...{
                              isEditMode,
                              handleChange,
                              values,
                              countryList,
                              cityList,
                              setFieldValue,
                              errors,
                              selectedCountry,
                              selectedCity,
                              setSelectedCity,
                              setSelectedCountry
                           }}
                        />
                     )}
                     {(isNgoMember || isManager) && (
                        <EditProfileNgoMember
                           {...{
                              isEditMode,
                              handleChange,
                              values,
                              genders,
                              setFieldValue,
                              errors,
                              touched
                           }}
                        />
                     )}
                     {isDonor && (
                        <EditProfileDonor
                           {...{
                              isEditMode,
                              handleChange,
                              values,
                              countryList,
                              setFieldValue,
                              errors,
                              touched
                           }}
                        />
                     )}
                  </div>
                  {isHumanitarian && (
                     <EditProfileHumanitarian
                        {...{
                           isEditMode,
                           handleChange,
                           values,
                           nations,
                           countryList,
                           setFieldValue,
                           handleOperatingCountries,
                           errors,
                           setSelectedCountry,
                           touched,
                           languageOptions,
                           languageLevelOptions
                        }}
                     />
                  )}
                  {isEditMode && (
                     <div className="footer-buttons">
                        <Button
                           className="edit-outlined"
                           onClick={() => handleCloseEditForm(resetForm)}
                           disabled={loading}
                        >
                           {t("cancel")}
                        </Button>
                        <Button
                           prefixCls=""
                           className="edit-button-workspace"
                           htmlType="submit"
                           form="editProfileForm"
                           loading={loading}
                           disabled={!dirty || loading}
                        >
                           {t("save-changes-button")}
                        </Button>
                     </div>
                  )}
               </Form>
            )}
         </Formik>
      </>
   );
};
export default EditProfileComponents;
